// src/components/dashboard/history/HistoryTable.js

import { FileText, X, Star } from "lucide-react";

/**
 * Dense row list for closed/unresolved incidents, mirroring the visual
 * grammar of IncidentQueueTable (severity/status accent stripe, one row per
 * record, actions on the right) instead of the card-grid layout the history
 * pages used before. Historical records don't carry a severity level, so the
 * accent stripe reflects outcome (resolved vs. unresolved) instead.
 */

function RatingStars({ rating }) {
  if (!rating) return <span className="text-xs text-slate-400">No rating</span>;
  return (
    <span className="inline-flex items-center gap-0.5" title={`${rating.toFixed(1)} / 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-3.5 w-3.5 ${
            star <= Math.round(rating) ? "fill-amber-400 text-amber-400" : "text-slate-300"
          }`}
        />
      ))}
    </span>
  );
}

function formatDate(ts) {
  if (!ts) return "—";
  try {
    if (typeof ts.toDate === "function") {
      return ts.toDate().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    }
    if (ts.seconds) {
      return new Date(ts.seconds * 1000).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    }
    return new Date(ts).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
}

function HistoryRow({ task, counterpartyField, onOpen, onViewReport, onDelete }) {
  const isUnresolved = task.status === "unresolved";

  return (
    <li
      className={`flex flex-wrap items-center gap-x-4 gap-y-2 border-l-2 px-4 py-3 transition hover:bg-slate-50 ${
        isUnresolved ? "border-l-amber-400" : "border-l-emerald-400"
      }`}
    >
      <div className="w-24 flex-shrink-0">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold ${
            isUnresolved
              ? "border-amber-200 bg-amber-50 text-amber-700"
              : "border-emerald-200 bg-emerald-50 text-emerald-700"
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${isUnresolved ? "bg-amber-500" : "bg-emerald-500"}`}
            aria-hidden="true"
          />
          {isUnresolved ? "Unresolved" : "Completed"}
        </span>
      </div>

      <button
        type="button"
        onClick={() => onOpen(task)}
        className="min-w-0 flex-1 basis-64 text-left"
      >
        <p className="truncate text-sm font-medium text-slate-900" title={task.title}>
          {task.title || "Untitled incident"}
        </p>
        <p className="mt-0.5 truncate text-xs text-slate-500">{task.domain || "General"}</p>
      </button>

      <div className="hidden w-40 flex-shrink-0 truncate text-sm text-slate-600 md:block">
        {task[counterpartyField] || "—"}
      </div>

      <div className="w-24 flex-shrink-0 text-sm text-slate-600">{formatDate(task.dateCompleted)}</div>

      <div className="hidden w-28 flex-shrink-0 sm:block">
        <RatingStars rating={task.rating} />
      </div>

      <div className="flex flex-shrink-0 items-center gap-1.5">
        {task.incidentReportId && (
          <button
            type="button"
            onClick={() => onViewReport(task.incidentReportId)}
            title="View resolution report"
            className="rounded-lg border border-slate-300 p-1.5 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
          >
            <FileText className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => onDelete(task.id)}
          title="Remove from history"
          className="rounded-lg border border-slate-300 p-1.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}

export default function HistoryTable({
  tasks = [],
  counterpartyField,
  onOpen,
  onViewReport,
  onDelete,
  emptyTitle = "No history yet",
  emptyBody,
}) {
  if (tasks.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white px-6 py-10 text-center">
        <p className="text-sm font-semibold text-slate-900">{emptyTitle}</p>
        {emptyBody && (
          <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">{emptyBody}</p>
        )}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="hidden flex-wrap items-center gap-x-4 border-b border-slate-100 bg-slate-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-slate-500 md:flex">
        <span className="w-24 flex-shrink-0">Outcome</span>
        <span className="min-w-0 flex-1 basis-64">Incident</span>
        <span className="w-40 flex-shrink-0">Counterparty</span>
        <span className="w-24 flex-shrink-0">Closed</span>
        <span className="hidden w-28 flex-shrink-0 sm:block">Rating</span>
        <span className="w-16 flex-shrink-0" aria-hidden="true" />
      </div>

      <ul className="divide-y divide-slate-100">
        {tasks.map((task) => (
          <HistoryRow
            key={task.id}
            task={task}
            counterpartyField={counterpartyField}
            onOpen={onOpen}
            onViewReport={onViewReport}
            onDelete={onDelete}
          />
        ))}
      </ul>
    </div>
  );
}
